import { Query, RouteNavigationAction } from '@varo/graphql-schema-types';
import { Href, Link, usePathname, useRouter } from 'expo-router';
import React, { ReactNode, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { parseGQLAnalyticsEvent } from 'services/analytics/helpers/parseGQLAnalyticsEvent';
import { FetchHomeTilesV2 } from 'services/graphql/queries/FetchHomeTilesV2';
import Button from 'shared/components/vxl/Button/Button';
import Icon from 'shared/components/vxl/icon/Icon/Icon';
import BodySmBoldText from 'shared/components/vxl/typography/primary/body/BodySmBoldText';
import VaroLogo from 'shared/components/vxl/VaroLogo/VaroLogo';
import homeTileTypes from 'shared/constants/homeTileTypes';
import {
  APP_MAX_WIDTH,
  DESKTOP_NAVIGATION_WIDTH,
} from 'shared/constants/layout';
import { useAnalyticsConfiguration } from 'shared/hooks/analytics/useAnalyticsConfiguration';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import HomeStrings from 'shared/localization/localizedStrings/HomeStrings';
import RewardsStrings from 'shared/localization/localizedStrings/RewardsStrings';
import { AuthContext, AuthContextProps } from 'shared/providers/auth';
import theme from 'shared/styles/theme';
import RouteOverrides from 'shared/utils/routeOverrides';
import { useQuery } from 'urql';

interface DesktopNavigationProps {
  children: ReactNode;
}

const ICON_SIZE = 20;
const VARO_LOGO_HEIGHT = 28;
const VARO_LOGO_WIDTH = 76;
const HOME_PATH = '/';
const MOVE_MONEY_PATH = '/move-money';
const REWARDS_PATH = '/rewards';
const MY_VARO_PATH = '/myvaro';

function DesktopNavigation({ children }: DesktopNavigationProps) {
  const currentPath = usePathname();
  const router = useRouter();
  const { segmentClient } = useAnalyticsConfiguration();

  const navigateToRoute = (action: RouteNavigationAction) => {
    const { route, analyticsEvent } = action;
    if (analyticsEvent) {
      const { name, properties } = parseGQLAnalyticsEvent(analyticsEvent);
      void segmentClient.logEvent(name, properties);
    }
    router.push((RouteOverrides(route) ?? route) as Href);
  };

  const [{ data, fetching }] = useQuery<Query>({
    query: FetchHomeTilesV2,
    variables: {
      tileTypes: homeTileTypes,
    },
    requestPolicy: 'cache-only',
    pause: currentPath !== HOME_PATH,
  });

  const navBtnConfig = data?.homeTilesV2?.navigationButton;

  const { handleLogout } = useContext<AuthContextProps>(AuthContext);

  const { t: translatedTextMoveMoney } = useTranslation('moveMoney', {
    keyPrefix: 'move',
  });

  const { t: translatedTextMyVaro } = useTranslation('myVaro');

  const logMyVaroTabClickedAnalytics = usePressableAnalytics({
    analyticsId: 'myVaroTabClicked',
  });

  const logRewardsTabClickedAnalytics = usePressableAnalytics({
    analyticsId: 'perkTabClicked',
  });

  return (
    <View style={styles.container}>
      <View style={styles.topNavContainer}>
        <VaroLogo height={VARO_LOGO_HEIGHT} width={VARO_LOGO_WIDTH} />
        {currentPath === HOME_PATH && (navBtnConfig || fetching) ? (
          <Button
            isLoading={fetching && !navBtnConfig}
            label={navBtnConfig?.label ?? ''}
            width="HUG"
            size="SMALL"
            onDark={false}
            type="SECONDARY"
            onPress={() => {
              if (navBtnConfig?.onPress) {
                navigateToRoute(navBtnConfig.onPress);
              }
            }}
          />
        ) : null}
      </View>
      <View style={styles.innerContainer}>
        <View style={styles.sideNavContainer}>
          <Link
            dismissTo
            href={HOME_PATH}
            style={[
              styles.navLink,
              currentPath === HOME_PATH && styles.activeNavLink,
            ]}
          >
            <Icon
              iconKey="HOME"
              iconColor={theme.color.text.primary}
              iconSize={ICON_SIZE}
            />
            <BodySmBoldText testID="tab-bar-label">
              {HomeStrings.title}
            </BodySmBoldText>
          </Link>
          <Link
            dismissTo
            href={MOVE_MONEY_PATH}
            style={[
              styles.navLink,
              currentPath === MOVE_MONEY_PATH && styles.activeNavLink,
            ]}
          >
            <Icon
              iconKey="MOVE_MONEY"
              iconColor={theme.color.text.primary}
              iconSize={ICON_SIZE}
            />
            <BodySmBoldText testID="tab-bar-label">
              {translatedTextMoveMoney('title')}
            </BodySmBoldText>
          </Link>
          <Link
            dismissTo
            href={REWARDS_PATH}
            style={[
              styles.navLink,
              currentPath === REWARDS_PATH && styles.activeNavLink,
            ]}
            onPress={() => logRewardsTabClickedAnalytics()}
          >
            <Icon
              iconKey="PERKS"
              iconColor={theme.color.text.primary}
              iconSize={ICON_SIZE}
            />
            <BodySmBoldText testID="tab-bar-label">
              {RewardsStrings.title}
            </BodySmBoldText>
          </Link>
          <Link
            dismissTo
            href={MY_VARO_PATH}
            style={[
              styles.navLink,
              currentPath === MY_VARO_PATH && styles.activeNavLink,
            ]}
            onPress={() => logMyVaroTabClickedAnalytics()}
          >
            <Icon
              iconKey="PROFILE"
              iconColor={theme.color.text.primary}
              iconSize={ICON_SIZE}
            />
            <BodySmBoldText testID="tab-bar-label">
              {translatedTextMyVaro('title')}
            </BodySmBoldText>
          </Link>
          <View style={styles.buttonContainer}>
            <Button
              label="Log out"
              type="SECONDARY"
              onPress={() => handleLogout()}
            />
          </View>
        </View>
        <View style={styles.appContainer}>{children}</View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  activeNavLink: {
    backgroundColor: theme.color.expressive.orchid,
  },
  appContainer: {
    flex: 1,
  },
  buttonContainer: {
    justifySelf: 'flex-end',
    marginTop: 'auto',
  },
  container: {
    flex: 1,
    marginHorizontal: 'auto',
    maxWidth: APP_MAX_WIDTH,
    width: '100%',
  },
  innerContainer: {
    flex: 1,
    flexDirection: 'row',
  },
  navLink: {
    alignItems: 'center',
    borderRadius: 4,
    display: 'flex',
    flexDirection: 'row',
    gap: 12,
    padding: 12,
  },
  sideNavContainer: {
    backgroundColor: theme.color.background.primary,
    borderRightColor: theme.color.border.primary,
    borderRightWidth: 1,
    flexDirection: 'column',
    height: '100%',
    justifyContent: 'flex-start',
    padding: theme.spacing.m,
    width: DESKTOP_NAVIGATION_WIDTH,
  },
  topNavContainer: {
    alignItems: 'center',
    backgroundColor: theme.color.background.primary,
    borderBottomColor: theme.color.border.primary,
    borderBottomWidth: 1,
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingHorizontal: theme.spacing.xl,
    paddingVertical: theme.spacing.m,
  },
});

export default DesktopNavigation;
